import {
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { createPortal } from "react-dom";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTranslation } from "react-i18next";
import { APP_FONT_FAMILY } from "../../constants/fonts";

type SetupCoachmarkProps = {
  open: boolean;
  title: string;
  subtitle: string;
  onDismiss: () => void;
  onNext?: () => void;
  nextLabel?: string;
  fullWidth?: boolean;
  /**
   * `menu` — no screen dim (safe inside MuiMenu).
   * `sidebar` | `default` — full-screen portal dim with a lit cutout on the target.
   */
  variant?: "default" | "menu" | "sidebar";
  children: ReactElement;
};

type Hole = { top: number; left: number; width: number; height: number };

function ScreenDimOverlay({
  hole,
  dim,
  zIndex,
}: {
  hole: Hole;
  dim: string;
  zIndex: number;
}) {
  return createPortal(
    <>
      {/* Dim everywhere except the hole (transparent box + huge shadow) */}
      <Box
        aria-hidden
        sx={{
          position: "fixed",
          top: hole.top,
          left: hole.left,
          width: hole.width,
          height: hole.height,
          borderRadius: "14px",
          boxShadow: `0 0 0 9999px ${dim}`,
          zIndex,
          pointerEvents: "none",
        }}
      />
      {/* Block clicks on dimmed regions */}
      <Box
        aria-hidden
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: Math.max(0, hole.top),
          zIndex,
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "fixed",
          top: hole.top + hole.height,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex,
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "fixed",
          top: hole.top,
          left: 0,
          width: Math.max(0, hole.left),
          height: hole.height,
          zIndex,
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "fixed",
          top: hole.top,
          left: hole.left + hole.width,
          right: 0,
          height: hole.height,
          zIndex,
        }}
      />
    </>,
    document.body,
  );
}

/**
 * Spotlight onboarding hint: dims the whole screen via a portal cutout,
 * keeps the target lit, and shows a coachmark tip.
 */
export function SetupCoachmark({
  open,
  title,
  subtitle,
  onDismiss,
  onNext,
  nextLabel,
  fullWidth = false,
  variant = "default",
  children,
}: SetupCoachmarkProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dim = alpha("#0f172a", 0.62);
  const isMenu = variant === "menu";
  const usePortalDim = !isMenu;
  const targetRef = useRef<HTMLDivElement>(null);
  const [hole, setHole] = useState<Hole | null>(null);

  useLayoutEffect(() => {
    if (!open || !usePortalDim) {
      setHole(null);
      return;
    }

    const measure = () => {
      const el = targetRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) return;
      const pad = variant === "sidebar" ? 6 : 8;
      setHole({
        top: r.top - pad,
        left: r.left - pad,
        width: r.width + pad * 2,
        height: r.height + pad * 2,
      });
    };

    measure();
    const raf = window.requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    const timer = window.setInterval(measure, 200);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      window.clearInterval(timer);
    };
  }, [open, usePortalDim, variant]);

  if (!open) {
    return children;
  }

  const tip = (
    <Box sx={{ position: "relative", textAlign: "center", pt: 0.5 }}>
      <IconButton
        aria-label={t("common.close")}
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
        sx={{
          position: "absolute",
          top: -10,
          insetInlineEnd: -10,
          color: "text.disabled",
          p: 0.5,
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Typography
        sx={{
          fontFamily: APP_FONT_FAMILY,
          fontSize: "0.9375rem",
          fontWeight: 600,
          lineHeight: 1.35,
          color: "text.primary",
          mb: 0.5,
          px: 2.5,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontFamily: APP_FONT_FAMILY,
          fontSize: "0.8125rem",
          fontWeight: 400,
          lineHeight: 1.45,
          color: "text.secondary",
          mb: onNext ? 1.5 : 0,
          px: 1,
        }}
      >
        {subtitle}
      </Typography>
      {onNext ? (
        <Button
          size="small"
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          sx={{ textTransform: "none", borderRadius: "10px", px: 2 }}
        >
          {nextLabel ?? t("selfSetup.tourNext")}
        </Button>
      ) : null}
    </Box>
  );

  const placement = isMenu || variant === "sidebar"
    ? isMobile
      ? "top"
      : "right"
    : isMobile
      ? "bottom"
      : "left";

  const tooltipSlotProps = {
    popper: {
      sx: { zIndex: (t: { zIndex: { modal: number } }) => t.zIndex.modal + 8 },
    },
    tooltip: {
      sx: {
        maxWidth: isMobile ? "calc(100vw - 32px)" : 420,
        width: "max-content",
        px: 2.5,
        py: 1.75,
        bgcolor: "background.paper",
        color: "text.primary",
        // Match dashboard StatCard/ChartCard radius (avoid theme multiplier).
        borderRadius: "14px",
        border: 1,
        borderColor: "divider",
        boxShadow: "0 10px 32px rgba(15, 23, 42, 0.16)",
      },
    },
    arrow: {
      sx: {
        color: "background.paper",
        "&::before": {
          border: `1px solid ${theme.palette.divider}`,
          boxSizing: "border-box",
        },
      },
    },
  };

  const dimZ = theme.zIndex.modal + 5;

  return (
    <>
      {usePortalDim && hole ? (
        <ScreenDimOverlay hole={hole} dim={dim} zIndex={dimZ} />
      ) : null}
      <Tooltip
        open
        arrow
        placement={placement}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        title={tip}
        slotProps={tooltipSlotProps}
      >
        <Box
          ref={targetRef}
          sx={{
            display: fullWidth || isMenu || variant === "sidebar" ? "block" : "inline-flex",
            width: fullWidth || isMenu || variant === "sidebar" ? "100%" : "auto",
            maxWidth: "100%",
            borderRadius: isMenu ? 0 : "14px",
            position: "relative",
            zIndex: 2,
            p: isMenu || usePortalDim ? (variant === "default" && !fullWidth ? 1.25 : 0) : 1.25,
            bgcolor: isMenu
              ? alpha(theme.palette.primary.main, 0.08)
              : "background.paper",
            outline: isMenu
              ? `2px solid ${theme.palette.primary.main}`
              : `3px solid ${theme.palette.common.white}`,
            outlineOffset: isMenu ? "-2px" : "2px",
            boxShadow: isMenu
              ? "none"
              : `0 8px 28px ${alpha(theme.palette.primary.main, 0.35)}`,
          }}
        >
          {children}
        </Box>
      </Tooltip>
    </>
  );
}
